import api from './api';
import { loadStripe } from '@stripe/stripe-js';

// Initialize Stripe with better error handling
const STRIPE_KEY = process.env.REACT_APP_STRIPE_PUBLIC_KEY || 'pk_test_0hn1tsQ1EdpAO04pd8NFi5wE00PsgP9qHt';

// Lazy load Stripe only when needed
let stripePromise: Promise<any> | null = null;

export const getStripePromise = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(STRIPE_KEY)
      .then(stripe => {
        return stripe;
      })
      .catch(error => {
        console.error("Failed to initialize Stripe:", error);
        return null;
      });
  }
  return stripePromise;
};

// Export for backward compatibility (deprecated - use getStripePromise instead)
export { getStripePromise as stripePromise };

// Types
export interface PaymentMethod {
  id: number;
  user_id: number;
  provider: string;
  type: string;
  card_brand: string;
  card_last_four: string;
  card_expiry_month: string;
  card_expiry_year: string;
  is_default: boolean;
  name: string;
  status: string;
  formatted_card: string;
  expiry_date: string;
  created_at: string;
  updated_at: string;
}

export interface PaymentMethodResponse {
  success: boolean;
  data: PaymentMethod;
}

export interface PaymentMethodsResponse {
  success: boolean;
  data: {
    data: PaymentMethod[];
  };
}

export interface DeletePaymentMethodResponse {
  success: boolean;
  message: string;
}

// Functions
const getPaymentMethods = async (): Promise<PaymentMethodsResponse> => {
  const response = await api.get('/payment-methods');
  return response.data;
};

const getPaymentMethod = async (paymentMethodId: number): Promise<PaymentMethodResponse> => {
  const response = await api.get(`/payment-methods/${paymentMethodId}`);
  return response.data;
};

const attachPaymentMethod = async (data: {
  provider: string;
  payment_method_id: string;
  name: string;
}): Promise<PaymentMethodResponse> => {
  const response = await api.post('/payment-methods', data);
  return response.data;
};

const deletePaymentMethod = async (paymentMethodId: number): Promise<DeletePaymentMethodResponse> => {
  const response = await api.delete(`/payment-methods/${paymentMethodId}`);
  return response.data;
};

const setDefaultPaymentMethod = async (paymentMethodId: number): Promise<PaymentMethodResponse> => {
  const response = await api.post(`/payment-methods/${paymentMethodId}/default`);
  return response.data;
};

// Function to create a new Stripe payment method using Elements
const createPaymentMethod = async (
  paymentMethodData: {
    type: 'card';
    card: any;
    billing_details?: {
      name?: string;
      email?: string;
      phone?: string;
      address?: {
        city?: string;
        country?: string;
        line1?: string;
        line2?: string;
        postal_code?: string;
        state?: string;
      };
    };
  },
  name: string
) => {
  // Get Stripe instance
  const stripe = await getStripePromise();
  if (!stripe) {
    throw new Error('Failed to load Stripe');
  }

  // Create payment method in Stripe
  const { paymentMethod, error } = await stripe.createPaymentMethod(paymentMethodData);

  if (error) {
    throw new Error(error.message);
  }

  if (!paymentMethod) {
    throw new Error('Failed to create payment method');
  }

  // Attach the payment method to our backend
  return attachPaymentMethod({
    provider: 'stripe',
    payment_method_id: paymentMethod.id,
    name,
  });
};

const paymentMethodService = {
  getPaymentMethods,
  getPaymentMethod,
  attachPaymentMethod,
  deletePaymentMethod,
  setDefaultPaymentMethod,
  createPaymentMethod,
};

export default paymentMethodService;
